/**
 * Cálculo del "grado mostrado" del investigador.
 *
 * Regla:
 * 1. Filtrar formaciones terminadas (enCurso=false, fechaTermino no nula).
 * 2. Filtrar solo las 3 categorías que cuentan como grado (pregrado, magíster, doctorado).
 *    Diplomado, especialidad, postítulo, postdoctorado, etc. NO generan grado.
 * 3. Tomar la de mayor peso. En empate de peso, la más reciente por fechaTermino.
 * 4. Devolver el LABEL legible de la categoría (no el titulo_obtenido).
 * 5. Si no hay ninguna, fallback al nombre legible de rol_investigador.
 * 6. Si tampoco hay rol_investigador, devolver string vacío.
 *
 * El peso vive hardcodeado acá porque es categoría estable que no cambia.
 */

interface FormacionLike {
	// Acepta tanto el shape del response público (`categoria`) como el editable (`categoriaFormacion`).
	categoria?: string;
	categoriaFormacion?: string;
	fechaTermino?: string | null;
	enCurso?: boolean;
}

function getCategoria(f: FormacionLike): string {
	return f.categoriaFormacion ?? f.categoria ?? '';
}

// Solo pregrado, magíster y doctorado generan grado (mismo criterio que la formación
// mostrada). Diplomado, especialidad, postítulo, etc. NO cuentan como grado.
const PESO_CATEGORIA: Record<string, number> = {
	doctorado: 3,
	magister: 2,
	pregrado: 1,
};

const LABEL_CATEGORIA: Record<string, string> = {
	doctorado: 'Doctor',
	magister: 'Magíster',
	pregrado: 'Pregrado',
};

function parseDateMs(value?: string | null): number {
	if (!value) return Number.NEGATIVE_INFINITY;
	const ms = Date.parse(`${value}T00:00:00Z`);
	return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

export function computeGradoFromFormaciones(
	formaciones: FormacionLike[],
	rolInvestigadorNombre?: string | null,
): string {
	const elegibles = formaciones.filter((f) => {
		if (f.enCurso === true) return false;
		if (!f.fechaTermino) return false;
		return PESO_CATEGORIA[getCategoria(f)] !== undefined;
	});

	if (elegibles.length > 0) {
		const ganadora = elegibles.slice().sort((a, b) => {
			const pesoDiff = (PESO_CATEGORIA[getCategoria(b)] ?? 0) - (PESO_CATEGORIA[getCategoria(a)] ?? 0);
			if (pesoDiff !== 0) return pesoDiff;
			return parseDateMs(b.fechaTermino) - parseDateMs(a.fechaTermino);
		})[0];
		return LABEL_CATEGORIA[getCategoria(ganadora)] ?? '';
	}

	if (rolInvestigadorNombre) {
		return rolInvestigadorNombre;
	}

	return '';
}
